/**
 * @fileoverview Shared liveQuery subscription lifecycle for the local-first
 *               services: resolve the host DB first, then subscribe a liveQuery
 *               whose observed function reads a RAW Dexie table as its first
 *               tracked operation.
 * @module local-db/services/watchDexieTable
 *
 * Responsibilities:
 * - Own the open-then-subscribe lifecycle once for every host watcher
 *   (tenant-web, system-web): cancellation before the open resolves, error
 *   forwarding for failed opens, and the `unsubscribe`/`ready` handle shape.
 *
 * Dependencies:
 * - dexie (liveQuery)
 *
 * Architecture:
 * - A `liveQuery(() => serviceCall())` subscription never re-emits when the
 *   service's first operation is a non-Dexie await (e.g. `getDb()`), because
 *   the table read escapes Dexie's observation zone. `open` therefore runs
 *   BEFORE the subscription is created, and `read` must touch the raw table
 *   (`raw.table('<name>')`) as its first tracked operation — matching the
 *   `useTemplates.ts` reference pattern and the livequery-contract rule.
 *
 * Notes:
 * - `ready` resolves (never rejects) so callers that only use callbacks do not
 *   produce unhandled rejections; open failures reach `handlers.error` instead.
 */

import { liveQuery } from 'dexie';
import type Dexie from 'dexie';
import type { Table } from 'dexie';

/**
 * @contract DexieWatchSubscription
 * Purpose: Handle returned by every liveQuery-backed watcher.
 * Fields:  unsubscribe (safe before ready resolves); ready — resolves once the
 *          open attempt finishes (subscription installed, cancelled, or failed).
 */
export interface DexieWatchSubscription {
  unsubscribe(): void;
  ready: Promise<void>;
}

/**
 * @contract DexieWatchHandlers
 * Purpose: Standard observer callbacks for table watchers.
 * Fields:  next receives each emitted value; error is optional.
 */
export interface DexieWatchHandlers<T> {
  next: (items: T) => void;
  error?: (err: unknown) => void;
}

/**
 * @contract WatchDexieTableOptions
 * Purpose: Host-injected inputs for watchDexieTable.
 * Fields:  open — opens/peeks the host DB (e.g. `peekDb() ?? getDb()`);
 *          getRaw — returns the raw Dexie handle or null while closed;
 *          table — the object store name the liveQuery must track;
 *          read — pure read over that raw table (first tracked operation).
 */
export interface WatchDexieTableOptions<T> {
  open: () => Promise<unknown>;
  getRaw: () => Dexie | null;
  table: string;
  read: (table: Table<any, any>) => Promise<T>;
  /** Emitted when the open resolved but the raw handle is closed again. */
  emptyValue: T;
}

/**
 * @process watchDexieTable
 * Steps:   1. `open()` resolves so `getRaw()` is non-null.
 *          2. Unless cancelled meanwhile, subscribe a `liveQuery` whose first
 *          call reads `raw.table(table)` — keeping Dexie inside the
 *          observation zone — then run `read`.
 *          3. Emit `read` results on every tracked change until `unsubscribe`.
 * Inputs:  WatchDexieTableOptions + DexieWatchHandlers.
 * Outputs: DexieWatchSubscription.
 * Failure Handling: a closed raw handle emits `emptyValue` until the DB
 *          reopens (host close→reopen recovery reloads/remounts the page and
 *          re-subscribes); a failed open is forwarded to `handlers.error`;
 *          `ready` always resolves.
 */
export function watchDexieTable<T>(
  options: WatchDexieTableOptions<T>,
  handlers: DexieWatchHandlers<T>,
): DexieWatchSubscription {
  let sub: { unsubscribe(): void } | undefined;
  let cancelled = false;
  const ready = options
    .open()
    .then(() => {
      if (cancelled) return; // unsubscribed while the DB was opening
      sub = liveQuery(async () => {
        const raw = options.getRaw();
        if (!raw) return options.emptyValue; // DB closed; empty until reopen
        return options.read(raw.table(options.table));
      }).subscribe(handlers);
    })
    .catch((err: unknown) => {
      // A failed open never reaches the liveQuery error callback; forward it so
      // callers relying on handlers.error surface it, and keep `ready` resolved
      // to avoid an unhandled rejection for callers that never await it.
      handlers.error?.(err);
    });
  return {
    unsubscribe: () => {
      cancelled = true;
      sub?.unsubscribe();
    },
    ready,
  };
}
